import React from 'react';
import { XIcon } from 'lucide-react';
import { Density, FormConfig } from '../../types';
import { fieldCatalog } from '../../utils/config';
import { Segmented, Select, Toggle } from './AdminControls';
interface LayoutTabProps {
  config: FormConfig;
  onChange: (config: FormConfig) => void;
}
export function LayoutTab({
  config,
  onChange
}: LayoutTabProps) {
  const fieldOptions = Object.values(fieldCatalog).map(f => ({
    value: f.id,
    label: f.label
  })).sort((a, b) => a.label.localeCompare(b.label));
  const availableMetrics = fieldOptions.filter(o => !config.headerMetricIds.includes(o.value));
  const addMetric = (id: string) => {
    if (!id || config.headerMetricIds.length >= 4) return;
    onChange({
      ...config,
      headerMetricIds: [...config.headerMetricIds, id]
    });
  };
  const removeMetric = (id: string) => {
    onChange({
      ...config,
      headerMetricIds: config.headerMetricIds.filter(m => m !== id)
    });
  };
  return <div className="space-y-5" data-id="element-15" data-mp="15oosvtcwy7cm">
      <section data-id="element-16" data-mp="1fm80owlrjo0l">
        <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-17" data-mp="1fmrfw5k13yax">
          Density
        </h3>
        <p className="mb-2 text-xs text-slate-500" data-id="element-18" data-mp="1auzra0kfo2nf">
          Controls padding and spacing across every field on the record.
        </p>
        <Segmented<Density> ariaLabel="Form density" value={config.density} options={[{
        value: 'comfortable',
        label: 'Comfortable'
      }, {
        value: 'compact',
        label: 'Compact'
      }]} onChange={v => onChange({
        ...config,
        density: v
      })} data-id="element-19" data-mp="15ubwr4wzx3og" />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white px-3 py-1" data-id="element-20" data-mp="1ommu6j8vs9f6">
        <Toggle label="Show record header" description="The borrower banner, status pill, and summary metrics." checked={config.showHeader} onChange={v => onChange({
        ...config,
        showHeader: v
      })} data-id="element-21" data-mp="1klumhufnqcl0" />
        <div className="border-t border-slate-100" data-id="element-22" data-mp="94uku87f6leb">
          <Toggle label="Show field search" description="Lets users filter the record by field name." checked={config.showSearch} onChange={v => onChange({
          ...config,
          showSearch: v
        })} data-id="element-23" data-mp="anull3q9se24" />
        </div>
      </section>

      <section data-id="element-24" data-mp="rrj1wriaden">
        <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-id="element-25" data-mp="5o876qypgrrq">
          Header metrics
        </h3>
        <p className="mb-2 text-xs text-slate-500" data-id="element-26" data-mp="3o1g4j6ivgu1m">
          Choose up to 4 fields to pin as summary metrics at the top of the record.
        </p>
        <div className="mb-2 flex flex-wrap gap-1.5" data-id="element-27" data-mp="3svahlr3z2arq">
          {config.headerMetricIds.length === 0 && <span className="text-xs italic text-slate-400" data-id="element-28" data-mp="3e2gvvny3wyem">No metrics pinned.</span>}
          {config.headerMetricIds.map(id => <span key={id} className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-1 pl-2.5 pr-1 text-xs font-medium text-blue-700" data-id="element-29" data-mp="354odgax39an0">
              {fieldCatalog[id]?.label ?? id}
              <button type="button" aria-label={`Remove ${fieldCatalog[id]?.label ?? id} metric`} onClick={() => removeMetric(id)} className="flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-blue-200" data-id="element-30" data-mp="2qj0qkhzi3s0g">
                <XIcon className="h-3 w-3" data-id="element-31" data-mp="2ukuoiiibahwx" />
              </button>
            </span>)}
        </div>
        {config.headerMetricIds.length < 4 && <Select value="" ariaLabel="Add header metric" onChange={addMetric} options={[{
        value: '',
        label: 'Add a metric…'
      }, ...availableMetrics]} data-id="element-32" data-mp="34fwefgkevo0k" />}
      </section>
    </div>;
}